import { driver, useInvokeAction } from "@leagueplatform/masonry";
import { Box, Button } from "@leagueplatform/genesis-core";
import { MasonryCheckboxRenderer } from "@leagueplatform/masonry-renderers";
import { toastActions, TOAST_STATUS } from "@leagueplatform/toast-messages";
import { APP_MANAGED_ROUTES } from "common/app-managed-routes";
import { MASONRY } from "common/constants";
import { notifyMasonrySubmitSuccess } from "masonry/submit-success-bus";
import type { ComponentProps, MouseEvent } from "react";

type ActionHandler = Parameters<
  typeof driver.actionController.registerHandler
>[2];

const SUCCESS_MODAL_TO_ROUTE: Record<string, string> = {
  [MASONRY.ROOT_NODE_ID_COMMUNICATION_PREFERENCES_DOC_PREFS_SUCCESS]:
    APP_MANAGED_ROUTES.COMMUNICATION_PREFERENCES_DOC_PREFS_SUCCESS,
  [MASONRY.ROOT_NODE_ID_COMMUNICATION_PREFERENCES_NOTIFICATIONS_SUCCESS]:
    APP_MANAGED_ROUTES.COMMUNICATION_PREFERENCES_NOTIFICATIONS_SUCCESS,
};

// GDS spacing tokens honored on `paddings.start`. `end`/`top`/`bottom` are
// accepted but ignored — `start` is applied symmetrically to both
// horizontal edges.
const PADDING_INLINE_TOKEN: Record<string, string> = {
  none: "$none",
  quarter: "$quarter",
  half: "$half",
  threeQuarters: "$threeQuarters",
  one: "$one",
  oneAndHalf: "$oneAndHalf",
  two: "$two",
};

type CheckboxRendererProps = ComponentProps<typeof MasonryCheckboxRenderer> & {
  paddings?: {
    start?: string;
    end?: string;
    top?: string;
    bottom?: string;
  };
  showItemDividers?: boolean;
  onTooltipClick?: Parameters<ReturnType<typeof useInvokeAction>>[0];
};

// The shared Masonry field wrappers only draw a help trigger when the node
// has inline `tooltipContent` (popover) and ignore `onTooltipClick`. When
// BE sends the action instead (e.g. `showModalWithProperties`), we render
// the trigger ourselves inside the group label, pinned to the end of the
// label row. Kept as its own component
// so `EmblemCheckboxRenderer` stays hook-free.
const EmblemTooltipActionLabel = ({
  label,
  tooltipIcon,
  tooltipLabel,
  onTooltipClick,
}: {
  label: string;
  tooltipIcon: string;
  tooltipLabel?: string;
  onTooltipClick: NonNullable<CheckboxRendererProps["onTooltipClick"]>;
}) => {
  const invokeAction = useInvokeAction();
  return (
    <Box
      as="span"
      css={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "$one",
        width: "100%",
      }}
    >
      <span>{label}</span>
      <Button
        icon={tooltipIcon}
        quiet
        hideLabel
        priority="secondary"
        size="small"
        css={{ flexShrink: 0 }}
        onClick={(event: MouseEvent<HTMLButtonElement>) =>
          invokeAction({ ...onTooltipClick, event })
        }
      >
        {tooltipLabel}
      </Button>
    </Box>
  );
};

// Thin horizontal divider between checkbox rows when BE sets
// `showItemDividers: true`. Color/thickness match `MasonryDividerRenderer`
// defaults (`onSurfaceBorderSubdued`, thin).
const ITEM_DIVIDER_CSS = {
  "& .GDS-checkbox-row:not(:last-child)": {
    borderBlockEnd: "1px solid $onSurfaceBorderSubdued",
    paddingBlockEnd: "$one",
    marginBlockEnd: "$one",
  },
};

// Cancels the Genesis checkbox's intrinsic 8px horizontal inset on the
// `<label class="GDS-checkbox-label">` so BE-supplied `paddings.start`
// aligns the row with sibling body content. Vertical padding is left
// intact so row height doesn't change. Also injects a between-row border
// when BE sets `showItemDividers: true`.
const EmblemCheckboxRenderer = ({
  paddings,
  showItemDividers,
  css,
  onTooltipClick,
  formFieldProperties,
  ...rest
}: CheckboxRendererProps) => {
  const token = paddings?.start;
  const inset = token != null ? PADDING_INLINE_TOKEN[token] : undefined;
  const mergedCss = {
    ...(css ?? {}),
    ...(inset != null
      ? { "& label.GDS-checkbox-label": { paddingInline: inset } }
      : {}),
    ...(showItemDividers ? ITEM_DIVIDER_CSS : {}),
  };
  const hasActionTooltip = Boolean(
    onTooltipClick &&
      formFieldProperties?.tooltipIcon &&
      !formFieldProperties.tooltipContent
  );
  const fieldProperties =
    hasActionTooltip && formFieldProperties
      ? {
          ...formFieldProperties,
          // Suppress the wrapper's own (content-gated) tooltip; the label
          // below carries the action-driven trigger instead.
          tooltipIcon: undefined,
          label: (
            <EmblemTooltipActionLabel
              label={formFieldProperties.label}
              tooltipIcon={formFieldProperties.tooltipIcon as string}
              tooltipLabel={formFieldProperties.tooltipLabel}
              onTooltipClick={
                onTooltipClick as NonNullable<typeof onTooltipClick>
              }
            />
          ) as unknown as string,
        }
      : formFieldProperties;
  return (
    <MasonryCheckboxRenderer
      {...rest}
      formFieldProperties={fieldProperties}
      css={mergedCss}
    />
  );
};

let registered = false;

export const registerEmblemMasonryActions = () => {
  if (registered) return;
  registered = true;

  type SubmitFormWithToastAndModalPayload = {
    formId?: string;
    formProcessorId?: string;
    onSuccessModalId?: string;
    onSuccessDeepLink?: string;
    errorMessage?: string;
  };

  const submitFormWithToastAndModalHandler = async (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    action: any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resources: any
  ) => {
    const payload = (action.payload ??
      {}) as SubmitFormWithToastAndModalPayload;
    const { formId, formProcessorId, onSuccessModalId, errorMessage } = payload;

    // Do NOT forward `onSuccessDeepLink` to the SDK's built-in `submitForm`
    // — when present it short-circuits success without exposing the data
    // processor's error status, which suppresses the error toast on failure.
    const result = (await resources.invokeAction({
      type: "submitForm",
      namespace: "default",
      payload: {
        formId,
        formProcessorId,
        suppressErrorModal: true,
      },
      ancestry: action.ancestry,
      event: action.event,
    })) as { isSuccess?: boolean } | undefined;

    if (result?.isSuccess) {
      if (onSuccessModalId) {
        const url = SUCCESS_MODAL_TO_ROUTE[onSuccessModalId];
        if (url && resources.navigate) {
          // Notify dirty-form guards before the PUSH so the blocker
          // doesn't false-fire on the success deep-link.
          notifyMasonrySubmitSuccess();
          resources.navigate(url);
        }
      }
    } else {
      toastActions.add({
        type: TOAST_STATUS.ERROR,
        text: errorMessage || "Something went wrong. Please try again.",
      });
    }

    return result ?? { isSuccess: false };
  };

  driver.actionController.registerHandler(
    "submitFormWithToastAndModal",
    "default",
    submitFormWithToastAndModalHandler as unknown as ActionHandler
  );

  // Only honor relative in-app paths from the YAML `payload.url` — Contentful
  // ships absolute URLs that don't resolve to a web route, so anything else
  // falls back to the CommPrefs hub.
  const dropNavigationDeeplinkHandler = async (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    action: any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resources: any
  ) => {
    if (resources.navigate) {
      const url = action?.payload?.url;
      const target =
        typeof url === "string" && url.startsWith("/")
          ? url
          : APP_MANAGED_ROUTES.COMMUNICATION_PREFERENCES;
      resources.navigate(target);
    }
    return { isSuccess: true };
  };

  driver.actionController.registerHandler(
    "deeplink",
    "dropNavigation",
    dropNavigationDeeplinkHandler as unknown as ActionHandler
  );

  // Masonry action for tiles that emit `openLiveChat` directly (rather
  // than a `/live-chat?entryPoint=...` deeplink). Delegates to the
  // shared `openChatSession` — one START_CHAT (or resume) + visibility
  // choreography used by every live-chat entry point (see the docstring
  // in `chat-instance.ts`). Dynamic import keeps `@leagueplatform/chat`'s
  // module-load renderer registrations out of this file's test surface.
  const openLiveChatHandler = async () => {
    const { openChatSession } = await import("live-chat/chat-instance");
    openChatSession();
    return { isSuccess: true };
  };

  driver.actionController.registerHandler(
    "openLiveChat",
    "default",
    openLiveChatHandler as unknown as ActionHandler
  );

  driver.nodeRendererRegistry.overrideRenderer(
    "checkboxGroup",
    "default",
    EmblemCheckboxRenderer as typeof MasonryCheckboxRenderer
  );
};
